import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import {
  useGetMotiocompAnalyticsQuery,
  useDeleteMotiocompClientErrorsMutation,
  MotiocompAnalyticsData,
  MotiocompClientErrorRow,
  MotiocompKpiBreakdownRow,
} from '../store/api/motiocompAnalyticsApi'

const CLIENT_ERRORS_PAGE_SIZE = 20

function getBrowserTimeZoneLabel(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local'
  } catch {
    return 'local'
  }
}

function formatClientErrorTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  const timeZone = getBrowserTimeZoneLabel()
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone,
    timeZoneName: 'short',
  })
}

type ParsedClientErrorDetails = {
  sectionLabel: string
  previewLine: string
  fullText: string
}

function extractWhereFromDetailsPlainText(text: string): string | null {
  const vm = text.match(/"view"\s*:\s*"([^"\\]*)"/)
  if (vm?.[1]) return vm[1].replace(/\./g, ' › ')
  const pm = text.match(/"pathname"\s*:\s*"([^"\\]*)"/)
  if (pm?.[1]) return pm[1]
  return null
}

function parseClientErrorDetails(details: string | null | undefined): ParsedClientErrorDetails {
  const fullText = (details ?? '').trim()
  if (!fullText) {
    return { sectionLabel: '—', previewLine: '—', fullText: '' }
  }
  let sectionLabel = '—'
  try {
    const o = JSON.parse(fullText) as Record<string, unknown>
    const view = typeof o.view === 'string' && o.view.trim() ? o.view.trim() : ''
    const pathname = typeof o.pathname === 'string' && o.pathname.trim() ? o.pathname.trim() : ''
    const path = typeof o.path === 'string' && o.path.trim() ? o.path.trim() : ''
    const href = typeof o.href === 'string' && o.href.trim() ? o.href.trim() : ''
    const url = typeof o.url === 'string' && o.url.trim() ? o.url.trim() : ''
    if (view) sectionLabel = view.replace(/\./g, ' › ')
    else if (pathname) sectionLabel = pathname
    else if (path) sectionLabel = path
    else if (href) sectionLabel = href.length > 80 ? `${href.slice(0, 77)}…` : href
    else if (url) sectionLabel = url.length > 80 ? `${url.slice(0, 77)}…` : url
  } catch {
    const recovered = extractWhereFromDetailsPlainText(fullText)
    if (recovered) sectionLabel = recovered
  }
  const firstBreak = fullText.indexOf('\n')
  const firstLine = (firstBreak === -1 ? fullText : fullText.slice(0, firstBreak)).trim()
  const previewLine = firstLine.length > 140 ? `${firstLine.slice(0, 137)}…` : firstLine || '—'
  return { sectionLabel, previewLine, fullText }
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null || seconds <= 0) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m > 60) {
    const h = Math.floor(m / 60)
    return `${h}h ${m % 60}m`
  }
  return `${m}m ${s}s`
}

function formatPercent(part: number, total: number): string {
  if (!total) return '0%'
  return `${((part / total) * 100).toFixed(1)}%`
}

function MotiocompTargetTable({
  title,
  rows,
  visitorMode,
  emptyMessage,
}: {
  title: string
  rows: MotiocompKpiBreakdownRow[]
  visitorMode: 'unique' | 'total'
  emptyMessage: string
}) {
  if (!rows.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="font-semibold text-gray-800 mb-1">{title}</h3>
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    )
  }
  const useUnique = visitorMode === 'unique'
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h3 className="font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-xs text-gray-500 mb-2">
        {useUnique ? 'Unique users' : 'Total events'} — toggle with Visitors control above.
      </p>
      <ul className="space-y-1.5 text-sm">
        {rows.map((r) => {
          const v = useUnique ? r.unique : r.total
          const denom = rows.reduce((s, x) => s + (useUnique ? x.unique : x.total), 0)
          return (
            <li key={r.key} className="flex justify-between gap-2">
              <span className="text-gray-600 truncate" title={r.key}>
                {r.label}
              </span>
              <span className="shrink-0 text-right">
                <strong>{v}</strong>
                <span className="text-gray-400 text-xs ml-1">{formatPercent(v, denom)}</span>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function formatSourceLabel(source: string): string {
  if (!source) return 'direct_or_unknown'
  if (source === 'direct_or_unknown') return source
  try {
    const url = new URL(source)
    return `${url.hostname}${url.pathname === '/' ? '' : url.pathname}`
  } catch {
    return source.length > 55 ? `${source.slice(0, 52)}...` : source
  }
}

function ClientErrorsTable({ rows }: { rows: MotiocompClientErrorRow[] }) {
  const [page, setPage] = useState(1)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const selectAllHeaderRef = React.useRef<HTMLInputElement>(null)
  const [deleteClientErrors, { isLoading: isDeleting }] = useDeleteMotiocompClientErrorsMutation()
  const tzLabel = useMemo(() => getBrowserTimeZoneLabel(), [])

  const totalPages = Math.max(1, Math.ceil(rows.length / CLIENT_ERRORS_PAGE_SIZE))
  const pageSafe = Math.min(page, totalPages)
  const start = (pageSafe - 1) * CLIENT_ERRORS_PAGE_SIZE
  const pageRows = useMemo(() => rows.slice(start, start + CLIENT_ERRORS_PAGE_SIZE), [rows, start])
  const rowIdSet = useMemo(() => new Set(rows.map((r) => r.id)), [rows])

  useEffect(() => {
    setPage(1)
  }, [rows])

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>()
      for (const id of prev) {
        if (rowIdSet.has(id)) next.add(id)
      }
      return next.size === prev.size ? prev : next
    })
  }, [rowIdSet])

  useEffect(() => {
    const el = selectAllHeaderRef.current
    if (!el) return
    el.indeterminate = selectedIds.size > 0 && selectedIds.size < rows.length
  }, [selectedIds.size, rows.length])

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (rows.length === 0) return new Set()
      if (prev.size === rows.length) return new Set()
      return new Set(rows.map((r) => r.id))
    })
  }, [rows])

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return
    setDeleteError(null)
    const ids = Array.from(selectedIds)
    const msg = `Delete ${ids.length} error log${ids.length === 1 ? '' : 's'}? This cannot be undone.`
    if (!window.confirm(msg)) return
    try {
      await deleteClientErrors({ ids }).unwrap()
      setSelectedIds(new Set())
    } catch (e: unknown) {
      const m =
        typeof e === 'object' && e !== null && 'data' in e &&
        typeof (e as { data?: { message?: string } }).data?.message === 'string'
          ? (e as { data: { message: string } }).data.message
          : 'Delete failed.'
      setDeleteError(m)
    }
  }, [selectedIds, deleteClientErrors])

  const handleCopy = useCallback(async (id: string, text: string) => {
    const ok = await copyTextToClipboard(text)
    if (ok) {
      setCopiedId(id)
      window.setTimeout(() => setCopiedId(null), 2000)
    }
  }, [])

  const allSelected = rows.length > 0 && selectedIds.size === rows.length

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50">
        <div className="text-sm text-gray-600">
          {selectedIds.size > 0 ? (
            <span>
              <strong className="text-gray-900">{selectedIds.size}</strong> selected
            </span>
          ) : (
            <span>Select rows to delete</span>
          )}
        </div>
        <button
          type="button"
          disabled={selectedIds.size === 0 || isDeleting}
          onClick={() => void handleDeleteSelected()}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isDeleting ? 'Deleting…' : 'Delete selected'}
        </button>
      </div>
      {deleteError ? (
        <div className="px-4 py-2 text-sm text-red-700 bg-red-50 border-b border-red-100">{deleteError}</div>
      ) : null}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="pl-4 pr-1 py-2 w-10 text-left">
              <input
                ref={selectAllHeaderRef}
                type="checkbox"
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                checked={allSelected}
                onChange={toggleSelectAll}
                disabled={rows.length === 0}
                title="Select all errors in this list"
                aria-label="Select all errors"
              />
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Time</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Where</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Kind</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">User</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Message</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Details</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">IP</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {pageRows.map((row) => {
            const parsed = parseClientErrorDetails(row.details)
            const whereLabel =
              (row.where_section && String(row.where_section).trim()) || parsed.sectionLabel
            return (
              <tr key={row.id} className="hover:bg-gray-50 align-top">
                <td className="pl-4 pr-1 py-2 align-top w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    checked={selectedIds.has(row.id)}
                    onChange={() => toggleSelected(row.id)}
                    aria-label={`Select error ${row.id}`}
                  />
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap align-top">
                  {formatClientErrorTime(row.created_at)}
                </td>
                <td
                  className="px-4 py-2 text-xs text-gray-800 max-w-[220px] align-top truncate"
                  title={whereLabel !== '—' ? whereLabel : undefined}
                >
                  {whereLabel}
                </td>
                <td className="px-4 py-2 text-sm text-gray-800 align-top whitespace-nowrap">
                  {row.kind === 'landing' ? 'Landing' : 'Logged in'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700 max-w-[180px] break-all align-top">
                  {row.user_email || (row.user_id != null ? `user #${row.user_id}` : '—')}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 max-w-[240px] align-top break-words">
                  {row.message}
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 max-w-md align-top">
                  <div className="flex items-start gap-2">
                    <span className="font-mono break-all truncate min-w-0 flex-1" title={parsed.fullText || undefined}>
                      {parsed.previewLine}
                    </span>
                    {parsed.fullText ? (
                      <button
                        type="button"
                        onClick={() => handleCopy(row.id, parsed.fullText)}
                        className="shrink-0 p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        title="Copy full details"
                        aria-label="Copy full error details"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    ) : null}
                  </div>
                  {copiedId === row.id ? (
                    <span className="text-emerald-600 text-[10px] mt-0.5 inline-block">Copied</span>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap align-top">
                  {row.ip_address || '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {rows.length > CLIENT_ERRORS_PAGE_SIZE && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50 text-sm text-gray-600">
          <span>
            Page {pageSafe} of {totalPages} · {rows.length} total
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
            >
              Next
            </button>
          </div>
        </div>
      )}
      {rows.length > 0 && rows.length <= CLIENT_ERRORS_PAGE_SIZE && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
          {rows.length} error(s)
        </div>
      )}
      <p className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100 bg-white">
        Times use your browser timezone ({tzLabel}).
      </p>
    </div>
  )
}

const MotiocompAnalytics: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [visitorMode, setVisitorMode] = useState<'unique' | 'total'>('unique')

  const queryParams = useMemo(() => {
    const p: { dateFrom?: string; dateTo?: string } = {}
    if (dateFrom) p.dateFrom = dateFrom
    if (dateTo) p.dateTo = dateTo
    return p
  }, [dateFrom, dateTo])

  const { data: resp, isLoading, isError, refetch } = useGetMotiocompAnalyticsQuery(queryParams)
  const d: MotiocompAnalyticsData | undefined = resp?.data
  const isUniqueMode = visitorMode === 'unique'
  const sessionsCount = d ? (isUniqueMode ? d.summary.totalVisitors : d.summary.totalVisitorsAll) : 0
  const landingCount = d ? (isUniqueMode ? d.summary.landingVisitors : d.summary.landingVisitorsAll) : 0
  const accountsRegisteredCount = d
    ? (isUniqueMode ? d.summary.accountsRegistered : d.summary.accountsRegisteredAll)
    : 0
  const ticketsCompiledCount = d
    ? (isUniqueMode ? d.summary.ticketsCompiled : d.summary.ticketsCompiledAll)
    : 0
  const outputChecksCount = d
    ? (isUniqueMode ? d.summary.outputChecksRun : d.summary.outputChecksRunAll)
    : 0
  const sessionsTrendRows = d ? (isUniqueMode ? d.dailyTrend : d.dailyTrendAll) : []
  const landingTrendRows = d ? (isUniqueMode ? d.landingDailyTrend : d.landingDailyTrendAll) : []

  return (
    <div className="p-6 w-full max-w-[1800px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Motiocomp Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Motiocomp · by Motionode — funnel for /motiocomp landing, work pack compiles, and AI output
            checks.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
        >
          Refresh
        </button>
      </div>

      {/* Date range */}
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm text-gray-600">From</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        />
        <label className="text-sm text-gray-600">To</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        />
        <label className="text-sm text-gray-600 ml-2">Visitors</label>
        <select
          value={visitorMode}
          onChange={(e) => setVisitorMode(e.target.value as 'unique' | 'total')}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="unique">Unique</option>
          <option value="total">Total</option>
        </select>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => {
              setDateFrom('')
              setDateTo('')
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && <div className="animate-pulse h-64 bg-gray-200 rounded-lg" />}
      {isError && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-700 text-sm">
          Failed to load Motiocomp analytics data.
        </div>
      )}

      {d && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
            <KpiCard
              label={`/motiocomp Visitors (${isUniqueMode ? 'Unique' : 'Total'})`}
              value={landingCount}
            />
            <KpiCard
              label={`Sessions (${isUniqueMode ? 'Unique' : 'Total'})`}
              value={sessionsCount}
            />
            <KpiCard
              label={`Accounts registered (${isUniqueMode ? 'Unique' : 'Total'})`}
              value={accountsRegisteredCount}
              accent="text-emerald-800"
            />
            <KpiCard
              label={`Tickets compiled (${isUniqueMode ? 'Unique' : 'Total'})`}
              value={ticketsCompiledCount}
              accent="text-emerald-700"
            />
            <KpiCard
              label={`Output checks (${isUniqueMode ? 'Unique' : 'Total'})`}
              value={outputChecksCount}
              accent="text-purple-700"
            />
            <KpiCard label="Avg Duration" value={formatDuration(d.summary.avgDurationSeconds)} />
            <KpiCard label="Total Actions" value={d.summary.totalActivityEvents} />
            <KpiCard label="Avg Actions/Session" value={d.summary.avgActionsPerSession} />
            <KpiCard
              label="Client errors (range)"
              value={d.clientErrors?.length ?? 0}
              accent="text-red-700"
            />
          </div>

          {/* Compile / output check breakdown by AI target */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Compiles & checks per AI target</h2>
            <p className="text-sm text-gray-500 mb-3">
              How users are routing work packs (Cursor, Claude Code, Copilot, Devin, human dev) and where
              they paste output back for review.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MotiocompTargetTable
                title="Tickets compiled per target"
                rows={d.compileTargets}
                visitorMode={visitorMode}
                emptyMessage="No compiles in this range."
              />
              <MotiocompTargetTable
                title="Output checks per target"
                rows={d.outputCheckTargets}
                visitorMode={visitorMode}
                emptyMessage="No output checks in this range."
              />
            </div>
          </section>

          {/* Client errors */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Errors</h2>
            <p className="text-sm text-gray-500 mb-3">
              User-visible failures and React crashes from Motiocomp (/motiocomp, setup, portal). Landing
              rows are pre-login (IP only); logged-in rows include account email when available.
            </p>
            {d.clientErrors && d.clientErrors.length > 0 ? (
              <ClientErrorsTable rows={d.clientErrors} />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-6 text-sm text-gray-500 text-center">
                No client errors recorded in this date range.
              </div>
            )}
          </section>

          {/* /motiocomp landing visitors trend */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Daily Visitors</h2>
            <p className="text-sm text-gray-500 mb-3">
              {isUniqueMode ? 'Unique sessions' : 'Total events'} per day reaching{' '}
              <span className="font-medium">/motiocomp</span>
            </p>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
              {landingTrendRows.length > 0 ? (
                <SimpleLineChart rows={landingTrendRows} strokeClass="stroke-indigo-500" />
              ) : (
                <div className="h-[180px] flex items-center justify-center text-sm text-gray-500">
                  No /motiocomp visitor data yet for this date range.
                </div>
              )}
            </div>
          </section>

          {/* Daily sessions trend */}
          {sessionsTrendRows.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Daily Sessions</h2>
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 overflow-x-auto">
                <div
                  className="flex items-end gap-1"
                  style={{ minWidth: Math.max(sessionsTrendRows.length * 28, 200) }}
                >
                  {(() => {
                    const max = Math.max(...sessionsTrendRows.map((r) => r.visitor_count), 1)
                    return sessionsTrendRows.map((r, i) => (
                      <div
                        key={i}
                        className="flex flex-col items-center flex-1 min-w-[24px]"
                        title={`${r.date}: ${r.visitor_count}`}
                      >
                        <div
                          className="w-5 bg-emerald-500 rounded-t"
                          style={{ height: Math.max(4, (r.visitor_count / max) * 120) }}
                        />
                        <span className="text-[9px] text-gray-400 mt-1">{r.date.slice(5)}</span>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {d.deviceBreakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Devices</h3>
                <ul className="space-y-1.5 text-sm">
                  {d.deviceBreakdown.map((r, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="text-gray-600">{r.device}</span>
                      <strong>{r.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {d.locationBreakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Top Countries</h3>
                <ul className="space-y-1.5 text-sm">
                  {d.locationBreakdown.slice(0, 10).map((r, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="text-gray-600">{r.country}</span>
                      <strong>{r.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {d.cityBreakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Top Cities</h3>
                <ul className="space-y-1.5 text-sm">
                  {d.cityBreakdown.slice(0, 10).map((r, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span
                        className="text-gray-600 truncate"
                        title={`${r.city}, ${r.region}, ${r.country}`}
                      >
                        {r.city}, {r.country}
                      </span>
                      <strong>{r.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {d.firstSteps.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-3">First Actions</h3>
                <ul className="space-y-1.5 text-sm">
                  {d.firstSteps.map((r, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="text-gray-600">{r.label}</span>
                      <strong>{r.session_count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {d.regionBreakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Top Regions</h3>
                <ul className="space-y-1.5 text-sm">
                  {d.regionBreakdown.slice(0, 12).map((r, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="text-gray-600 truncate">
                        {r.region}, {r.country}
                      </span>
                      <strong>{r.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {d.sourceBreakdown.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Top Sources</h3>
                <ul className="space-y-1.5 text-sm">
                  {d.sourceBreakdown.slice(0, 12).map((r, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="text-gray-600 truncate" title={r.source}>
                        {formatSourceLabel(r.source)}
                      </span>
                      <strong>{r.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {d.lastSteps.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Last Actions (drop-off points)</h2>
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <ul className="space-y-1.5 text-sm">
                  {d.lastSteps.map((r, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="text-gray-600">{r.label}</span>
                      <strong>{r.session_count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {d.actionStats.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-3">All Motiocomp Actions</h2>
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                        Action
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">
                        Total
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">
                        Unique Users
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {d.actionStats.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-800">{r.label}</td>
                        <td className="px-4 py-2 text-sm text-right font-medium">{r.total_occurrences}</td>
                        <td className="px-4 py-2 text-sm text-right text-gray-600">{r.unique_users}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function KpiCard({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</div>
    </div>
  )
}

function SimpleLineChart({
  rows,
  strokeClass,
}: {
  rows: { date: string; visitor_count: number }[]
  strokeClass?: string
}) {
  const width = 780
  const height = 220
  const padLeft = 42
  const padRight = 14
  const padTop = 10
  const padBottom = 24
  const chartW = width - padLeft - padRight
  const chartH = height - padTop - padBottom
  const maxValue = Math.max(0, ...rows.map((r) => r.visitor_count))
  const maxY = Math.max(1, maxValue)
  const stepX = rows.length > 1 ? chartW / (rows.length - 1) : chartW
  const yTicks = maxY <= 2 ? maxY : Math.min(4, maxY)

  const points = rows.map((r, i) => {
    const x = padLeft + stepX * i
    const y = padTop + chartH - (r.visitor_count / maxY) * chartH
    return { ...r, x, y }
  })
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const yTickValues = [...Array(yTicks + 1)].map((_, i) => {
    const raw = (maxY / yTicks) * i
    return maxY <= 4 ? Math.round(raw) : Math.round(raw * 10) / 10
  })
  const yUniqueTickValues = yTickValues.filter((v, i) => i === 0 || v !== yTickValues[i - 1])

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[680px] h-[240px]">
        {yUniqueTickValues.map((v, i) => {
          const ratio = maxY === 0 ? 0 : v / maxY
          const y = padTop + chartH - chartH * ratio
          return (
            <g key={i}>
              <line
                x1={padLeft}
                y1={y}
                x2={width - padRight}
                y2={y}
                className="stroke-gray-200"
                strokeDasharray="3 3"
              />
              <text x={padLeft - 8} y={y + 4} textAnchor="end" className="fill-gray-500 text-[10px]">
                {v}
              </text>
            </g>
          )
        })}

        {points.map((p, i) => (
          <text
            key={i}
            x={p.x}
            y={height - 6}
            textAnchor="middle"
            className="fill-gray-500 text-[10px]"
          >
            {p.date.slice(5)}
          </text>
        ))}

        <path d={pathD} fill="none" className={strokeClass || 'stroke-emerald-500'} strokeWidth={2.5} />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={2.5}
            className={strokeClass ? strokeClass.replace('stroke-', 'fill-') : 'fill-emerald-500'}
          />
        ))}
      </svg>
    </div>
  )
}

export default MotiocompAnalytics
